import { Controller } from "@hotwired/stimulus"

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Command-palette search, in two flavours.
//
//   SERVER mode  (urlValue present): opens a native <dialog>, debounces typing
//   into a Turbo Frame (id="search_results") that loads rich results, and keeps
//   keyboard nav over the freshly-loaded rows. This is the global navbar palette.
//
//   CLIENT mode  (no urlValue): the rows are server-rendered up front (e.g. the
//   compare-with opponent list). search() filters those rows in place by their
//   data-search-text attribute; no fetching happens.
//
// Both modes share arrow/enter navigation over the VISIBLE rows and Escape.
//
// When shortcutValue is true (navbar instance only) the controller binds the
// global ⌘K / Ctrl+K shortcut and listens for an "arena:open-search" custom
// event so on-page buttons can open the palette from anywhere.
export default class extends Controller<HTMLElement> {
  static targets = ["dialog", "input", "panel", "result", "empty"]
  static values = { url: String, shortcut: Boolean }

  declare readonly dialogTarget: HTMLDialogElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly panelTarget: HTMLElement
  declare readonly resultTargets: HTMLAnchorElement[]
  declare readonly hasDialogTarget: boolean
  declare readonly hasEmptyTarget: boolean
  declare readonly emptyTarget: HTMLElement
  declare readonly urlValue: string
  declare readonly hasUrlValue: boolean
  declare readonly shortcutValue: boolean

  private debounce?: number
  private activeIndex = -1
  private onWindowKeydown = (event: KeyboardEvent) => this.maybeOpenFromShortcut(event)
  private onOpenSearch = () => this.open()
  // Delegated so it catches both real mouse clicks on a result and the
  // synthetic .click() the Enter key fires (see onKeydown).
  private onResultClick = (event: MouseEvent) => this.trackResultSelect(event)
  private onBeforeCache = () => this.closeIfOpen()
  private onPageShow = (event: PageTransitionEvent) => { if (event.persisted) this.closeIfOpen() }

  connect() {
    if (this.shortcutValue) {
      window.addEventListener("keydown", this.onWindowKeydown)
      window.addEventListener("arena:open-search", this.onOpenSearch)
    }
    // A back/forward restore (Turbo snapshot or native bfcache) can re-show this
    // page with the dialog still flagged open but no longer truly modal — Esc,
    // backdrop, and search all feel dead. Strip it from the Turbo snapshot
    // before caching, and close it on a bfcache restore, so the next open()
    // re-enters via showModal() in a clean modal state.
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    window.addEventListener("pageshow", this.onPageShow)
    this.element.addEventListener("click", this.onResultClick)
    this.closeIfOpen()
  }

  disconnect() {
    if (this.shortcutValue) {
      window.removeEventListener("keydown", this.onWindowKeydown)
      window.removeEventListener("arena:open-search", this.onOpenSearch)
    }
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    window.removeEventListener("pageshow", this.onPageShow)
    this.element.removeEventListener("click", this.onResultClick)
    if (this.debounce) window.clearTimeout(this.debounce)
  }

  // Fire once a result is actually chosen — the navigation itself is already a
  // pageview, so this only adds the query → result attribution that the bare
  // pageview loses.
  private trackResultSelect(event: MouseEvent) {
    const anchor = (event.target as HTMLElement).closest("a")
    if (!anchor || !this.resultTargets.includes(anchor as HTMLAnchorElement)) return
    try {
      window.umami?.track("arena-search-select", {
        href: anchor.getAttribute("href") || "",
        query: this.inputTarget.value.trim(),
        position: this.visibleResults.indexOf(anchor as HTMLAnchorElement),
        mode: this.isServerMode ? "server" : "client",
      })
    } catch {}
  }

  private closeIfOpen() {
    if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close()
  }

  // True when results are loaded from the server as the user types.
  private get isServerMode(): boolean {
    return this.hasUrlValue && this.urlValue.length > 0
  }

  // ⌘K / Ctrl+K opens the palette from anywhere.
  private maybeOpenFromShortcut(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault()
      this.open()
    }
  }

  open() {
    if (this.dialogTarget.open) return
    try { window.umami?.track("arena-search-open", { mode: this.isServerMode ? "server" : "client" }) } catch {}
    this.dialogTarget.showModal()
    this.inputTarget.value = ""
    this.activeIndex = -1
    if (this.isServerMode) {
      this.loadResults("")
    } else {
      // Reset any prior filter so the full list shows on reopen.
      this.filterRows("")
    }
    // Focus after the dialog paints so the cursor lands in the field.
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  onClose() {
    if (this.debounce) window.clearTimeout(this.debounce)
  }

  // Close only on a true backdrop click: a native <dialog> reports
  // event.target === the dialog element when the ::backdrop is clicked. Using
  // the target directly (not "outside the panel") avoids closing when a
  // text-selection drag inside the input happens to release over the backdrop.
  backdropClose(event: MouseEvent) {
    if (event.target === this.dialogTarget) this.close()
  }

  search() {
    const query = this.inputTarget.value
    if (this.isServerMode) {
      if (this.debounce) window.clearTimeout(this.debounce)
      this.debounce = window.setTimeout(() => this.loadResults(query), 160)
    } else {
      this.filterRows(query)
    }
  }

  // CLIENT mode: substring-filter the pre-rendered rows in place.
  private filterRows(query: string) {
    const q = query.trim().toLowerCase()
    let visible = 0

    this.resultTargets.forEach((row) => {
      const haystack = (row.dataset.searchText || "").toLowerCase()
      const match = q === "" || haystack.includes(q)
      row.classList.toggle("hidden", !match)
      if (match) visible += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visible > 0)
    }

    this.activeIndex = -1
    this.clearActive()
  }

  private loadResults(query: string) {
    const frame = this.frame
    if (!frame) return
    frame.src = `${this.urlValue}?q=${encodeURIComponent(query.trim())}`
  }

  // SERVER mode: reset the active row whenever fresh results arrive.
  onResults() {
    this.activeIndex = -1
    this.clearActive()
  }

  onKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter": {
        const active = this.visibleResults[this.activeIndex]
        if (active) {
          event.preventDefault()
          active.click()
        }
        break
      }
      case "Escape":
        // A type="search" input swallows the FIRST Escape to clear its own
        // value (Chromium/Safari native behaviour), so the keypress never
        // reaches the <dialog>'s cancel handling and the modal feels stuck
        // open. Close it ourselves. preventDefault stops the field-clear so a
        // single Escape always dismisses the palette regardless of contents.
        event.preventDefault()
        this.close()
        break
    }
  }

  private move(delta: number) {
    const results = this.visibleResults
    if (results.length === 0) return
    this.activeIndex = (this.activeIndex + delta + results.length) % results.length
    this.clearActive()
    const active = results[this.activeIndex]
    active.classList.add("is-active")
    active.scrollIntoView({ block: "nearest" })
  }

  // Only rows that are currently visible (CLIENT mode hides filtered-out rows).
  private get visibleResults(): HTMLAnchorElement[] {
    return this.resultTargets.filter((el) => !el.classList.contains("hidden"))
  }

  private clearActive() {
    this.resultTargets.forEach((el) => el.classList.remove("is-active"))
  }

  private get frame(): HTMLElement | null {
    return this.element.querySelector("turbo-frame#search_results")
  }
}
